// src/services/telemetryService.js
// Servicio unificado de telemetría, métricas de embudo y rastreo dual (GTM/GA4 y Backend PostgreSQL)

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const SESSION_KEY = 'xf_telemetry_session_id';
const SOURCE_KEY = 'xf_traffic_source';

/**
 * Obtiene o inicializa el identificador de sesión anónimo persistente para el aficionado
 * @returns {string}
 */
export function getOrCreateSessionId() {
  try {
    let sessionId = sessionStorage.getItem(SESSION_KEY) || localStorage.getItem(SESSION_KEY);
    if (!sessionId) {
      sessionId = 'xf-' + Date.now().toString(36) + '-' + Math.random().toString(36).substring(2, 9);
      sessionStorage.setItem(SESSION_KEY, sessionId);
      localStorage.setItem(SESSION_KEY, sessionId);
    }
    return sessionId;
  } catch (e) {
    return 'xf-fallback-' + Date.now().toString(36);
  }
}

/**
 * Detecta y almacena la fuente de tráfico proveniente de la URL (?x=b para boleto, ?x=a para acceso)
 * @returns {string} 'b', 'a', o 'direct'
 */
export function detectAndStoreTrafficSource() {
  try {
    if (typeof window !== 'undefined' && window.location) {
      const urlParams = new URLSearchParams(window.location.search);
      const xParam = urlParams.get('x');
      if (xParam) {
        const cleanSource = xParam.trim().toLowerCase();
        sessionStorage.setItem(SOURCE_KEY, cleanSource);
        return cleanSource;
      }
    }
    const stored = sessionStorage.getItem(SOURCE_KEY);
    return stored || 'direct';
  } catch (e) {
    return 'direct';
  }
}

/**
 * Envía un evento al embudo de métricas:
 * 1. window.dataLayer para Google Tag Manager / GA4
 * 2. API Backend de PostgreSQL para auditoría interna y dashboard
 * 
 * @param {string} eventName - Nombre del evento ('qr_scan', 'page_view', 'form_start', 'register_success', 'pass_download', 'pass_screenshot', 'pass_lookup_search', 'pass_lookup_success', 'pass_lookup_not_found')
 * @param {object} [customData] - Datos adicionales opcionales ({ ticketFolio, trafficSource, metadata })
 */
export function trackMetric(eventName, customData = {}) {
  const sessionId = getOrCreateSessionId();
  const trafficSource = customData.trafficSource || detectAndStoreTrafficSource();
  const ticketFolio = customData.ticketFolio || null;

  // 1. Google Tag Manager / GA4 dataLayer push
  try {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'xolofest_' + eventName,
        event_category: 'XoloFest2026',
        traffic_source: trafficSource,
        session_id: sessionId,
        ticket_folio: ticketFolio,
        timestamp: new Date().toISOString(),
        ...customData
      });

      // Si gtag está disponible directamente
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'xolofest_' + eventName, {
          traffic_source: trafficSource,
          session_id: sessionId,
          ticket_folio: ticketFolio
        });
      }
    }
  } catch (err) {
    console.debug('Error enviando a dataLayer:', err);
  }

  // 2. Registro interno en backend PostgreSQL
  try {
    const payload = {
      eventName,
      trafficSource,
      sessionId,
      ticketFolio,
      metadataJson: customData.metadata ? JSON.stringify(customData.metadata) : null
    };

    fetch(`${API_BASE_URL}/public/telemetry/metric`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    }).catch(err => {
      // Telemetry must be silent and non-blocking
      console.debug('Silently ignored telemetry network err:', err);
    });
  } catch (err) {
    console.debug('Silently caught telemetry exception:', err);
  }
}

/**
 * Registra listeners de captura de pantalla (PrintScreen y visibilidad/blur en pases)
 * @param {Function} onScreenshotDetected - Callback al detectar captura
 * @returns {Function} Función para desuscribir los listeners
 */
export function attachScreenshotListeners(onScreenshotDetected) {
  if (typeof window === 'undefined') return () => {};

  let lastCapture = 0;
  const triggerCapture = (method) => {
    const now = Date.now();
    // Evitar ráfagas duplicadas en menos de 2 segundos
    if (now - lastCapture > 2000) {
      lastCapture = now;
      if (typeof onScreenshotDetected === 'function') {
        onScreenshotDetected(method);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'PrintScreen' || e.keyCode === 44) {
      triggerCapture('printscreen_key');
    }
  };

  const handleVisibilityChange = () => {
    // Al presionar combinaciones de captura en teléfonos móviles, la app cambia de foco brevemente
    if (document.visibilityState === 'hidden') {
      triggerCapture('mobile_visibility_screenshot');
    }
  };

  window.addEventListener('keyup', handleKeyDown);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    window.removeEventListener('keyup', handleKeyDown);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}
